import React, { useEffect, useMemo, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import ApiBaseURL from '../ApiBaseUrl/ApiBaseURL';
import { useNavigate } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const DashboardCubicMeter = ({
    vehicleTypeId = null,
    vendorId = null,
    driverId = null,
    vehicleId = null,
    // period = '3m'
    period = null
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedRange, setSelectedRange] = useState(period);
    const [fuelData, setFuelData] = useState({});

    const navigate = useNavigate();

    const monthNames = [
        '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    useEffect(() => {
        fetchFuelSummary();
    }, [vehicleTypeId, vendorId, driverId, vehicleId, selectedRange, period]);

    const fetchFuelSummary = async () => {
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('token');

        if (!userId || !token) {
            toast.error('Missing userId or token in localStorage');
            return;
        }

        try {
            const resp = await fetch(`${ApiBaseURL}/dashboard/v1/get/cubic-summary`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    userId,
                    Authorization: `Bearer ${token}`
                },
                // body: JSON.stringify({
                //     vehicleTypeId,
                //     vendorId,
                //     driverId,
                //     vehicleId,
                //     // period: selectedRange.toLowerCase()
                //     period
                // })
                body: JSON.stringify({
                    vehicleTypeId,
                    vendorId,
                    driverId,
                    vehicleId,
                    // period: selectedRange.toLowerCase()
                    period
                })
            });

            const result = await resp.json();
            if (resp.ok && result.statusDescription.statusCode === 200) {
                setFuelData(result.data || {});
            } else if ([401, 403].includes(result.statusDescription.statusCode)) {
                toast.error(result.statusDescription.description || "Unauthorized access. Redirecting...");
                localStorage.clear();

                setTimeout(() => {
                    window.location.replace('/');
                }, 1500);
            } else {
                console.log(result.statusDescription.description || 'Failed to fetch fuel data');
            }
        } catch (err) {
            toast.error('Error fetching fuel summary: ' + err.message);
        }
    };

    const parsed = Object.entries(fuelData)
        .map(([key, items]) => {
            const item = items[0];
            const isDateKey = /^\d{4}-\d{2}-\d{2}$/.test(key);

            let label = '';
            let sortKey = 0;

            if (isDateKey) {
                const date = new Date(key);
                const day = date.getDate();
                const month = date.toLocaleString('en-GB', { month: 'short' });
                label = `${day} ${month}`;
                sortKey = date.getTime();
            } else {
                const monthIndex = parseInt(key, 10);
                const month = monthNames[monthIndex] || key;
                const yearShort = '25';
                label = `${month}'${yearShort}`;
                sortKey = monthIndex;
            }

            return {
                label,
                totalCubic: item?.totalCubic || 0,
                hardCubic: item?.hardCubic || 0,
                softCubic: item?.softCubic || 0,
                sortKey
            };
        })
        .sort((a, b) => a.sortKey - b.sortKey);

    // const parsed = Object.entries(fuelData)
    //     .map(([key, item]) => {
    //         const isDateKey = /^\d{4}-\d{2}-\d{2}$/.test(key);
    //         const label = isDateKey
    //             ? new Date(key).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) // e.g., "05 Jun"
    //             : monthNames[parseInt(key)];

    //         return {
    //             label,
    //             fuelQty: item.fuelQuantity || 0,
    //             sortKey: isDateKey ? new Date(key).getTime() : parseInt(key)
    //         };
    //     })
    //     .sort((a, b) => a.sortKey - b.sortKey);

    // const parsed = Object.entries(fuelData)
    //     .map(([key, item]) => {
    //         const isDateKey = /^\d{4}-\d{2}-\d{2}$/.test(key);
    //         let label = '';

    //         if (isDateKey) {
    //             const date = new Date(key);
    //             const month = date.toLocaleString('en-GB', { month: 'short' });
    //             const yearShort = date.getFullYear().toString().slice(-2);
    //             label = `${month}-${yearShort}`;
    //         } else {
    //             const monthIndex = parseInt(key, 10) - 1;
    //             const month = monthNames[monthIndex] || '';
    //             const yearShort = '25';
    //             label = `${month}-${yearShort}`;
    //         }

    //         return {
    //             label,
    //             fuelQty: item.fuelQuantity || 0,
    //             sortKey: isDateKey ? new Date(key).getTime() : parseInt(key),
    //         };
    //     })
    //     .sort((a, b) => a.sortKey - b.sortKey);

    // const labels = parsed.map(item => item.label);
    const datasetValues = parsed.map(item => Math.round(item.fuelQty));

    const labels = parsed.map(item => item.label);

    const data = {
        labels,
        datasets: [
            {
                label: 'Total Work',
                data: parsed.map(item => item.totalCubic),
                borderColor: 'rgb(28, 153, 235)',
                backgroundColor: 'rgba(12, 78, 121, 0.2)',
                tension: 0.4,
                borderWidth: 1
            },
            {
                label: 'Composite',
                data: parsed.map(item => item.hardCubic),
                borderColor: 'rgb(239, 62, 101)',
                backgroundColor: 'rgba(94, 7, 26, 0.2)',
                tension: 0.4,
                borderWidth: 1
            },
            {
                label: 'Rehandling',
                data: parsed.map(item => item.softCubic),
                borderColor: 'rgb(244, 185, 38)',
                backgroundColor: 'rgba(106, 80, 14, 0.2)',
                tension: 0.4,
                borderWidth: 1
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            tooltip: {
                mode: 'index',
                intersect: false
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: function (value) {
                        if (value >= 1_00_00_000) return (value / 1_00_00_000).toFixed(1).replace(/\.0$/, '') + 'Cr';
                        if (value >= 1_00_000) return (value / 1_00_000).toFixed(1).replace(/\.0$/, '') + 'L';
                        if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
                        if (value >= 1_000) return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
                        return value;
                    }
                }
            }
        }
    };

    const filterOptions = [
        { label: 'Year-25', value: 'y-25' },
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: 'Week', value: 'week' },
        { label: 'Current Month', value: 'cm' },
        { label: 'Prev. Month', value: 'pm' },
        { label: '3M', value: '3m' },
        { label: '6M', value: '6m' },
        { label: '9M', value: '9m' },
    ];

    const handleFilterSelect = (range) => {
        setSelectedRange(range);
        setShowDropdown(false);
    };

    const totals = useMemo(() => {
        return Object.values(fuelData).flat().reduce(
            (acc, item) => {
                acc.totalCubic += item.totalCubic || 0;
                acc.hardCubic += item.hardCubic || 0;
                acc.softCubic += item.softCubic || 0;
                return acc;
            },
            { totalCubic: 0, hardCubic: 0, softCubic: 0 }
        );
    }, [fuelData]);


    return (
        <div className="card shadow-sm my-0" style={{ maxWidth: '700px', margin: 'auto' }}>
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">Work Done Analysis In Cubic Meter</h5>
                    <div style={{ position: 'relative' }}>
                        <i
                            className="fa-solid fa-filter d-none"
                            style={{ cursor: 'pointer', fontSize: '18px' }}
                            onClick={() => setShowDropdown(!showDropdown)}
                        ></i>
                        {showDropdown && (
                            <div className="dropdown-menu show" style={{ position: 'absolute', right: 0, zIndex: 999 }}>
                                {filterOptions.map(({ label, value }) => (
                                    <button key={value} className="dropdown-item" onClick={() => handleFilterSelect(value)}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <Line data={data} options={options} />
            </div>

            <div className="row mb-3 text-center">
                <div className="col-md-4">
                    <h6 className="mb-1">Total Work</h6>
                    <p className="fw-bold text-primary mb-0">
                        {new Intl.NumberFormat('en-IN').format(Math.round(totals.totalCubic))} m³
                    </p>
                </div>

                <div className="col-md-4">
                    <h6 className="mb-1">Composite</h6>
                    <p className="fw-bold text-success mb-0">
                        {new Intl.NumberFormat('en-IN').format(Math.round(totals.hardCubic))} m³
                    </p>
                </div>

                <div className="col-md-4">
                    <h6 className="mb-1">Rehandling </h6>
                    <p className="fw-bold text-warning mb-0">
                        {new Intl.NumberFormat('en-IN').format(Math.round(totals.softCubic))} m³
                    </p>
                </div>
            </div>

        </div>
    );
};

export default DashboardCubicMeter;